import { defineStore } from "pinia";
import type { UserInfo, UserState } from "@/typings/types";
import { defaultSetting, getLocalState, setLocalState } from "./helper";

function clearSessionStorage() {
  sessionStorage.clear();
}

export const useUserStore = defineStore("user-store", {
  state: (): UserState => getLocalState(),

  actions: {
    setUserTier(tier: UserInfo["tier"]) {
      this.userInfo.tier = tier;
      this.recordState();
    },

    setReferralCode(referralCode: string) {
      if (this.userInfo.referralCode !== referralCode)
        this.userInfo.referralCode = referralCode;
      this.recordState();
    },
    setUserApiTier(apiTier: UserInfo["apiTier"]) {
      this.userInfo.apiTier = apiTier;
      this.recordState();
    },

    downGradeToFree() {
      if (this.userInfo.tier === "tier2") this.userInfo.tier = "free";
    },

    getUserTier(): UserInfo["tier"] {
      return this.userInfo.tier;
    },
    getApiTier(): UserInfo["apiTier"] {
      return this.userInfo.apiTier || "tier0";
    },

    getTotalPaidInUnit(): number {
      return this.userInfo.totalPaidInUnit || 0;
    },

    hasTierAccess(requiredTier: UserInfo["tier"]): boolean {
      const tierHierarchy = ["guest", "free", "tier1", "tier2", "tier3"];

      // Ensure the user's tier exists in the hierarchy
      const userTierIndex = tierHierarchy.indexOf(this.userInfo?.tier);
      if (userTierIndex === -1) {
        console.error(
          "User's tier is invalid or undefined:",
          this.userInfo?.tier
        );
        return false; // Consider returning false or handling this case as appropriate
      }

      // Ensure the required tier exists in the hierarchy
      const requiredTierIndex = tierHierarchy.indexOf(requiredTier);
      if (requiredTierIndex === -1) {
        console.error("Required tier is invalid:", requiredTier);
        return false; // Consider returning false or handling this case as appropriate
      }

      return userTierIndex >= requiredTierIndex;
    },
    updateUserInfo(userInfo: Partial<UserInfo>) {
      // Use Pinia's $patch for more efficient updates
      this.$patch((state) => {
        Object.keys(userInfo).forEach((key) => {
          const typedKey = key as keyof UserInfo;
          (state.userInfo[typedKey] as any) = userInfo[typedKey];
        });
      });
      this.recordState();
      return true;
    },
    setUUID(uuid: string) {
      this.userInfo.uuid = uuid;
      this.recordState();
    },

    getUUID(): string {
      return this.userInfo.uuid;
    },
    setTotalPaidInUnit(totalPaidInUnit: number) {
      this.userInfo.totalPaidInUnit = totalPaidInUnit;
    },

    setUserAsGuest(isGuest: boolean) {
      this.userInfo.isGuest = isGuest;
      // Set user tier according to guest status
      this.setUserTier(isGuest ? "guest" : "free");
      this.recordState();
    },

    isUserGuest() {
      return this.userInfo.isGuest;
    },

    setUserAsAuthenticated(isAuthenticated: boolean) {
      this.userInfo.isAuthenticated = isAuthenticated;
      this.recordState();
    },

    isUserAuthenticated() {
      // console.log('userInfo: ', this.userInfo);
      // console.log('isUserAuthenticated: ', this.userInfo.isAuthenticated);
      return this.userInfo.isAuthenticated;
    },

    resetUserInfo() {
      this.userInfo = { ...defaultSetting().userInfo };
      clearSessionStorage();
      this.recordState();
    },

    recordState() {
      setLocalState(this.$state);
    },

    // async clearAllUserData() {
    //   clearAllUserData();
    //   this.recordState();
    // }
  // NEW: Guest mode management
  setGuestMode(isGuest: boolean) {
  this.userInfo.isGuestMode = isGuest;
  this.recordState();
  },
 
  isInGuestMode(): boolean {
  return this.userInfo.isGuestMode || false;
  },
 
  // NEW: Permission checking
  canPerformAction(action: string): boolean {
  if (this.isUserAuthenticated()) return true;
  if (this.isInGuestMode()) {
  const guestAllowedActions = this.userInfo.guestPermissions || ['view', 'read', 'browse'];
  return guestAllowedActions.includes(action.toLowerCase());
  }
  return false;
  },
 
  // NEW: Session management
  setGuestSessionStart() {
  this.userInfo.guestSessionStart = Date.now();
  this.recordState();
  },
 
  getGuestSessionDuration(): number {
  if (!this.userInfo.guestSessionStart) return 0;
  return Date.now() - this.userInfo.guestSessionStart;
  },
 
  // NEW: Enhanced reset for guest mode
  resetToGuestMode() {
  const defaultSettings = defaultSetting();
  this.userInfo = {
  ...defaultSettings.userInfo,
  isGuestMode: true,
  guestSessionStart: Date.now(),
  };
  this.recordState();
  },
  },
 });
